import { Checkbox, Flex, FormControl, Text, VStack } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useMemo } from 'react';

import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { useAnalytics } from '@/context/Analytics';
import { EventListStatus } from '@/enums/EventListStatus';
import { LibraryFilter } from '@/enums/LibraryFilter';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResourceRelationship, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryEventFiltersInfinite } from '@/queries/algolia-filter-item';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { AlgoliaFilterItem } from '@/resources/AlgoliaFilterItem';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringArrayParams, StringParam } from '@/utils/url-query-string';

export const FilterReleaseYear = () => {
  const analytics = useAnalytics();

  const { filters, change } = useLibraryFilters({
    [LibraryFilter.YearStartsAt]: StringArrayParams,
  });

  const { query, slug } = useQueryParams<{
    query: string;
    slug: string;
  }>({
    query: StringParam,
    slug: StringParam,
  });

  const { data: hub } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(hub.id));

  const { data, error, size } = useResourcesInfinite(
    queryEventFiltersInfinite('release_years', {
      filters: {
        query,
        subject_id: subtreeIds,
        scope: EventListStatus.past,
        visible: 'true',
        library: 'true',
        ...filters,
      },
      pageSize: 30,
    }),
    {
      revalidateAll: false,
    }
  );

  const { items } = useMemo(
    () => extractSWRInfiniteData<AlgoliaFilterItem>(data as Array<Response<AlgoliaFilterItem>>, error, size),
    [data, error, size]
  );

  const currentFilters = filters[LibraryFilter.YearStartsAt] || [];

  const onUpdate = (item) => {
    let updatedFilters = [];

    if (currentFilters.includes(item)) {
      updatedFilters = currentFilters.filter((currentItem) => currentItem !== item);
    } else {
      updatedFilters = [...currentFilters, item];
    }
    change(
      { [LibraryFilter.YearStartsAt]: updatedFilters.join(',') || undefined },
      { method: 'replace', shallow: true, scroll: true }
    );
    analytics.track('library_year_filter', { release_year: item });
  };

  return (
    <FormControl px={4} as="fieldset">
      <VStack alignItems="flex-start">
        {items?.map(({ value, label, amount }) => {
          return (
            <Flex w="full" key={value}>
              <Checkbox
                isChecked={currentFilters.includes(value.toString())}
                name={LibraryFilter.YearStartsAt}
                value={value}
                colorScheme="violet"
                size="lg"
                onChange={() => {
                  onUpdate(value.toString());
                }}
              >
                <Text as="span" textStyle="groteskS">
                  {label} ({amount})
                </Text>
              </Checkbox>
            </Flex>
          );
        })}
      </VStack>
    </FormControl>
  );
};
